import { useMemo, useRef, useState } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import type { Tag } from '@tribeplatform/gql-client/types'
import { useSpaceTags } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { InlineMultiselect } from '@tribeplatform/react-ui-kit/Multiselect'

import { useDebounce } from '../../../common/hooks/useDebounce.js'
import { useI18n, T } from '../../../i18n/index.js'
import { INLINE_MULTI_SELECT_PAGE_SIZE } from '../../utils/inlineFilters.js'

interface InlineTagIdPickerProps {
  value: string[]
  className?: string
  onChange: (tagIds: string[]) => void
}
export const InlineTagIdPicker = ({
  value,
  className,
  onChange,
}: InlineTagIdPickerProps) => {
  const { $t } = useI18n()
  const dataRef = useRef<Tag[]>(undefined)
  const [searchInput, setSearchInput] = useState('')
  const query = useDebounce(searchInput, 500)

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isInitialLoading,
  } = useSpaceTags({
    variables: {
      limit: INLINE_MULTI_SELECT_PAGE_SIZE,
      query: searchInput ? query : '',
    },
  })

  const tags = useMemo(() => {
    const prevData = dataRef.current
    if (prevData && !data && isInitialLoading) {
      return prevData
    }

    if (!data && !prevData) {
      return []
    }

    const { nodes } = simplifyPaginatedResult<Tag>(data)
    dataRef.current = nodes

    return nodes
  }, [data, isInitialLoading])

  const handleClick = (tagId: string) => {
    if (value.includes(tagId)) {
      const newValue = value.filter(id => id !== tagId)
      onChange(newValue)
      return
    }

    const newValue = [...value, tagId]
    onChange(newValue)
  }

  return (
    <InlineMultiselect className={className}>
      <InlineMultiselect.SearchBar
        value={searchInput}
        placeHolder={$t({
          defaultMessage: 'Search...',
          id: 'Generics.SearchDotDotDot',
        })}
        onChange={setSearchInput}
      />
      <InlineMultiselect.Items isLoading={isInitialLoading}>
        {tags.length === 0 && isInitialLoading && (
          <div className="text-sm text-content-subdued flex justify-center">
            <SvgIcon className="animate-spin" size="lg" name="spinner" />
          </div>
        )}
        {!isInitialLoading && tags.length === 0 && (
          <div className="text-sm text-content-subdued">
            <T id="Generics.NoResults" defaultMessage="No results" />
          </div>
        )}
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchNextPage}
          useWindow={false}
          hasMore={hasNextPage ?? false}
        >
          {tags.map(tag => (
            <InlineMultiselect.Item
              key={tag.id}
              selected={value.includes(tag.id)}
              onSelect={() => handleClick(tag.id)}
            >
              <div className="truncate text-base">{tag.title}</div>
            </InlineMultiselect.Item>
          ))}
          {isFetchingNextPage && (
            <div className="flex justify-center">
              <SvgIcon className="animate-spin" size="lg" name="spinner" />
            </div>
          )}
        </InfiniteScroll>
      </InlineMultiselect.Items>
    </InlineMultiselect>
  )
}
